"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";

export const ProductShowcase = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: "0px"
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 px-4 sm:px-6 md:px-12 lg:px-20 bg-gradient-to-b from-background/50 to-background"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2
            className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            See It{" "}
            <span className="bg-gradient-primary bg-clip-text text-transparent">
              In Action
            </span>
          </h2>
          <p
            className={`text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto transition-all duration-700 delay-100 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            Connect your Supabase project in seconds and start exploring powerful analytics
          </p>
        </div>

        {/* Screenshots Grid */}
        <div className="space-y-20">
          {/* First Screenshot - Connect DB */}
          <div
            className={`transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <div className="mb-8 text-center">
              <h3 className="text-3xl font-bold mb-3 text-white">
                Quick & Secure Connection
              </h3>
              <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                Connect your Supabase database with OAuth in just one click. We only perform read-only operations to keep your data safe.
              </p>
            </div>
            <div className="relative rounded-2xl overflow-hidden border border-gray-700 shadow-2xl hover:shadow-primary/20 transition-shadow duration-300">
              <Image
                src="/connect-db.png"
                alt="Connect Your Supabase Project interface showing secure OAuth connection"
                width={1920}
                height={1080}
                className="w-full h-auto"
                priority
              />
            </div>
          </div>

          {/* Second Screenshot - Analytics Dashboard */}
          <div
            className={`transition-all duration-700 delay-300 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <div className="mb-8 text-center">
              <h3 className="text-3xl font-bold mb-3 text-white">
                AI-Powered Analytics Dashboard
              </h3>
              <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                Ask questions in natural language and get instant insights. Track key metrics like WAU, retention, and conversion rates with beautiful visualizations.
              </p>
            </div>
            <div className="relative rounded-2xl overflow-hidden border border-gray-700 shadow-2xl hover:shadow-primary/20 transition-shadow duration-300">
              <Image
                src="/learn-from-your-data.png"
                alt="Growth Analytics Chat and Metrics Dashboard with interactive charts"
                width={1920}
                height={1080}
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
